// Frontend AI Queue Client
// Handles job submission and status polling for AI requests
import { backendConfig } from '../config/backendConfig';
import { auth } from '../firebase';

class AIQueueClient {
  constructor() {
    this.backendBase = backendConfig.baseUrl;
    this.pollingIntervals = new Map(); // Track polling intervals
  }

  // Wait for Firebase auth to initialize and provide a UID
  async ensureUidAvailable(maxWaitMs = 3000) {
    const start = Date.now();
    const getUid = () => auth.currentUser?.uid || localStorage.getItem('uid');
    let uid = getUid();
    while (!uid && Date.now() - start < maxWaitMs) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, 100));
      uid = getUid();
    }
    if (uid) {
      // Keep localStorage in sync for fallback
      try { localStorage.setItem('uid', uid); } catch (_) {}
    }
    return uid;
  }

  // Get auth headers from session
  getAuthHeaders() {
    const sessionToken = localStorage.getItem('sessionToken');
    const tokenValid = sessionToken && sessionToken !== 'null' && sessionToken !== 'undefined';
    const uid = auth.currentUser?.uid || localStorage.getItem('uid');
    const headers = { 'Content-Type': 'application/json' };
    if (tokenValid) headers['Authorization'] = `Bearer ${sessionToken}`;
    if (uid) headers['x-user-id'] = uid;
    return headers;
  }

  async getResolvedUid(maxWaitMs = 12000) {
    const uid = (auth.currentUser?.uid) || (await this.ensureUidAvailable(maxWaitMs));
    if (uid) {
      try { localStorage.setItem('uid', uid); } catch (_) {}
    }
    return uid;
  }

  // Submit AI job to queue
  async submitJob(type, data) {
    try {
      const uid = await this.getResolvedUid();
      if (!uid && !localStorage.getItem('sessionToken')) {
        throw new Error('Authentication required');
      }
      // Fix double /api/api/ issue - backendBase already includes /api
      const baseUrl = this.backendBase.endsWith('/api') 
        ? this.backendBase 
        : `${this.backendBase}/api`;
      let url = `${baseUrl}/queue/submit`;
      if (uid) url += `${url.includes('?') ? '&' : '?'}userId=${encodeURIComponent(uid)}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ type, data })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to submit job');
      }

      const result = await response.json();
      console.log(`📝 Job ${result.jobId} submitted successfully`);
      return result;
    } catch (error) {
      console.error('Job submission error:', error);
      throw error;
    }
  }

  // Check job status
  async getJobStatus(jobId) {
    try {
      const uid = await this.getResolvedUid();
      if (!uid && !localStorage.getItem('sessionToken')) {
        throw new Error('Authentication required');
      }
      // Fix double /api/api/ issue - backendBase already includes /api
      const baseUrl = this.backendBase.endsWith('/api') 
        ? this.backendBase 
        : `${this.backendBase}/api`;
      let url = `${baseUrl}/queue/status/${jobId}`;
      if (uid) url += `${url.includes('?') ? '&' : '?'}userId=${encodeURIComponent(uid)}`;
      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get job status');
      }

      return await response.json();
    } catch (error) {
      console.error('Job status check error:', error);
      throw error;
    }
  }

  // Cancel job
  async cancelJob(jobId) {
    try {
      const uid = await this.getResolvedUid();
      if (!uid && !localStorage.getItem('sessionToken')) {
        throw new Error('Authentication required');
      }
      // Fix double /api/api/ issue - backendBase already includes /api
      const baseUrl = this.backendBase.endsWith('/api') 
        ? this.backendBase 
        : `${this.backendBase}/api`;
      let url = `${baseUrl}/queue/cancel/${jobId}`;
      if (uid) url += `${url.includes('?') ? '&' : '?'}userId=${encodeURIComponent(uid)}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel job');
      }

      // Stop polling for this job
      this.stopPolling(jobId);

      return await response.json();
    } catch (error) {
      console.error('Job cancellation error:', error);
      throw error;
    }
  }

  // Poll job status until completion
  async pollJobStatus(jobId, onProgress, onComplete, onError, maxAttempts = 150) {
    let attempts = 0;
    
    const poll = async () => {
      try {
        attempts++;
        const status = await this.getJobStatus(jobId);
        
        // Call progress callback
        if (onProgress) {
          onProgress(status);
        }

        // Check if job is complete
        if (status.state === 'completed') {
          this.stopPolling(jobId);
          if (onComplete) {
            onComplete(status.result);
          }
          return;
        }

        // Check if job failed
        if (status.state === 'failed') {
          this.stopPolling(jobId);
          if (onError) {
            onError(new Error(status.error || 'Job failed'));
          }
          return;
        }

        // Continue polling if not complete and within max attempts
        if (attempts < maxAttempts) {
          // Adaptive polling interval based on queue position
          const interval = this.calculatePollingInterval(status);
          this.pollingIntervals.set(jobId, setTimeout(poll, interval));
        } else {
          this.stopPolling(jobId);
          if (onError) {
            onError(new Error('Job polling timeout'));
          }
        }
      } catch (error) {
        this.stopPolling(jobId);
        if (onError) {
          onError(error);
        }
      }
    };

    // Start polling
    poll();
  }

  // Calculate adaptive polling interval
  calculatePollingInterval(status) {
    if (status.state === 'active') {
      return 2000; // Poll every 2 seconds when processing
    } else if (status.state === 'waiting') {
      // Poll less frequently when waiting
      const estimatedWait = status.estimatedWait || 30000;
      return Math.min(Math.max(estimatedWait / 10, 3000), 10000); // 3-10 seconds
    }
    return 5000; // Default 5 seconds
  }

  // Stop polling for a specific job
  stopPolling(jobId) {
    const interval = this.pollingIntervals.get(jobId);
    if (interval) {
      clearTimeout(interval);
      this.pollingIntervals.delete(jobId);
    }
  }

  // Stop all polling
  stopAllPolling() {
    this.pollingIntervals.forEach(interval => clearTimeout(interval));
    this.pollingIntervals.clear();
  }

  // Generate appreciation with queuing
  async generateAppreciation(data) {
    return new Promise((resolve, reject) => {
      this.submitJob('generate-appreciation', data)
        .then(job => {
          this.pollJobStatus(
            job.jobId,
            (status) => {
              // Progress callback
              console.log(`🔄 Appreciation job ${job.jobId}: ${status.state}`);
            },
            (result) => {
              // Success callback
              console.log(`✅ Appreciation generated successfully`);
              resolve(result);
            },
            (error) => {
              // Error callback
              console.error(`❌ Appreciation generation failed:`, error);
              reject(error);
            }
          );
        })
        .catch(reject);
    });
  }

  // Generate scene WITHOUT queue (direct call to backend)
  // This bypasses Redis/Bull so it works in local dev without queue infrastructure.
  async generateScene(data) {
    try {
      console.log('[AIQueue] generateScene called with data:', data);
      const response = await fetch(`${this.backendBase}/api/story-generation/enhanced-conceptualize-scene`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      console.log('[AIQueue] generateScene response status:', response.status, 'ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        console.error('[AIQueue] generateScene error response:', errorText);
        throw new Error(errorText || 'Failed to generate scene');
      }

      const result = await response.json();
      console.log('[AIQueue] generateScene result:', result);
      return result;
    } catch (error) {
      console.error('[AIQueue] generateScene failed:', error);
      throw error;
    }
  }

  // Ask mentor with queuing
  async askMentor(data) {
    return new Promise((resolve, reject) => {
      this.submitJob('ask-mentor', data)
        .then(job => {
          this.pollJobStatus(
            job.jobId,
            (status) => {
              console.log(`🔄 Mentor job ${job.jobId}: ${status.state}`);
            },
            (result) => {
              console.log(`✅ Mentor response received`);
              resolve(result);
            },
            (error) => {
              console.error(`❌ Mentor request failed:`, error);
              reject(error);
            }
          );
        })
        .catch(reject);
    });
  }

  // Get queue statistics
  async getQueueStats() {
    try {
      // Fix double /api/api/ issue - backendBase already includes /api
      const baseUrl = this.backendBase.endsWith('/api') 
        ? this.backendBase 
        : `${this.backendBase}/api`;
      const response = await fetch(`${baseUrl}/queue/stats`);
      if (!response.ok) {
        // Return fallback stats instead of throwing
        return {
          queue: {
            waiting: 0,
            active: 0,
            completed: 0,
            failed: 0,
            delayed: 0,
            total: 0
          },
          performance: {
            avgProcessingTime: 0,
            successRate: 100
          },
          timestamp: Date.now(),
          note: 'Queue system offline'
        };
      }
      return await response.json();
    } catch (error) {
      // Return fallback stats instead of throwing
      console.warn('Queue stats error (returning fallback):', error.message || error);
      return {
        queue: {
          waiting: 0,
          active: 0,
          completed: 0,
          failed: 0,
          delayed: 0,
          total: 0
        },
        performance: {
          avgProcessingTime: 0,
          successRate: 100
        },
        timestamp: Date.now(),
        note: 'Queue system offline - Network error'
      };
    }
  }

  // Get user's recent jobs
  async getMyJobs(limit = 10, offset = 0) {
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const uid = await this.getResolvedUid();
      if (!uid && !sessionToken) {
        return {
          jobs: [],
          total: 0,
          limit,
          offset,
          note: 'Authentication not ready'
        };
      }
      // Fix double /api/api/ issue - backendBase already includes /api
      const baseUrl = this.backendBase.endsWith('/api') 
        ? this.backendBase 
        : `${this.backendBase}/api`;
      let url = `${baseUrl}/queue/my-jobs?limit=${limit}&offset=${offset}`;
      // Always include UID for backend fallbacks and clearer association
      if (uid) url += `&userId=${encodeURIComponent(uid)}`;
      const response = await fetch(url, { headers: this.getAuthHeaders() });
      
      if (!response.ok) {
        // Return fallback data instead of throwing
        return {
          jobs: [],
          total: 0,
          limit,
          offset,
          note: 'Queue system offline'
        };
      }
      
      return await response.json();
    } catch (error) {
      // Return fallback data instead of throwing
      console.warn('Get user jobs error (returning fallback):', error.message || error);
      return {
        jobs: [],
        total: 0,
        limit,
        offset,
        note: 'Queue system offline - Network error'
      };
    }
  }

  // Check queue health
  async checkHealth() {
    try {
      // Fix double /api/api/ issue - backendBase already includes /api
      const baseUrl = this.backendBase.endsWith('/api') 
        ? this.backendBase 
        : `${this.backendBase}/api`;
      const response = await fetch(`${baseUrl}/queue/health`);
      if (!response.ok) {
        throw new Error('Queue health check failed');
      }
      return await response.json();
    } catch (error) {
      console.error('Queue health check error:', error);
      throw error;
    }
  }
}

// Create singleton instance
const aiQueue = new AIQueueClient();

// Clean up polling intervals on page unload
window.addEventListener('beforeunload', () => {
  aiQueue.stopAllPolling();
});

export default aiQueue; 